import { motion } from 'framer-motion';
import { useState } from 'react';
import React from 'react';
import PageTransition from '../components/common/PageTransition';
import VideoCard from '../components/video/VideoCard';
import VideoEmbed from '../components/video/VideoEmbed';
import VideoGrid from '../components/video/VideoGrid';
import SEO from '../components/common/SEO';
import { staggerContainer, staggerItem } from '../utils/animations';
import { videos as initialVideos } from '../data/videos';
import { FaWhatsapp, FaYoutube } from 'react-icons/fa';

const videoStats = [
  { icon: '🎥', value: '50+', label: 'Videos', color: '#D32F2F' },
  { icon: '👀', value: '10K+', label: 'Views', color: '#1565C0' },
  { icon: '❤️', value: '5K+', label: 'Likes', color: '#E91E63' },
  { icon: '👥', value: '1K+', label: 'Subscribers', color: '#3CB044' },
];

const videoTypes = [
  { icon: '📖', title: 'Story Time', description: 'Animated stories and read-alongs that bring our magazine tales to life!', color: '#7B2FBE' },
  { icon: '🧩', title: 'Puzzle Challenges', description: 'Interactive puzzle videos where you can play along and test your skills!', color: '#1565C0' },
  { icon: '🎨', title: 'Art & Crafts', description: 'Step-by-step tutorials for fun DIY projects and creative activities.', color: '#F57C00' },
  { icon: '🔬', title: 'Science Fun', description: 'Easy science experiments you can try at home with common materials.', color: '#00897B' },
  { icon: '🎬', title: 'Behind the Scenes', description: 'See how we create The Tiny Times and meet our amazing team!', color: '#D32F2F' },
  { icon: '🏆', title: 'Contest Winners', description: 'Celebrating our talented readers and their amazing submissions!', color: '#3CB044' },
];

const categoryInfo = {
  all: { icon: '📺', label: 'All Videos' },
  stories: { icon: '📖', label: 'Stories' },
  puzzles: { icon: '🧩', label: 'Puzzles' },
  activities: { icon: '🎨', label: 'Activities' },
  behindthescenes: { icon: '🎬', label: 'Behind the Scenes' },
  tutorials: { icon: '📝', label: 'Tutorials' },
};

const Video = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = activeCategory === 'all'
    ? initialVideos
    : initialVideos.filter(video => video.category === activeCategory);

  const categories = ['all', ...new Set(initialVideos.map(v => v.category))];
  const featuredVideo = initialVideos.find(v => v.featured) || initialVideos[0];

  return (
    <PageTransition>
      <SEO
        title="Fun Learning Videos - Video Corner"
        description="Watch educational, animated, and fun story videos curated for children to complement their weekly magazine reading experience."
        keywords="educational videos kids, children story videos, animated puzzles, kids learning youtube, active learning kids"
      />
      <div className="min-h-screen">

        {/* Hero */}
        <section className="relative py-24 md:py-36 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #FFEBEE 0%, #FFF9E6 40%, #F3EEFF 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(211,47,47,0.12) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute top-0 left-0 w-96 h-96 opacity-15 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(211,47,47,0.3), transparent 70%)', filter: 'blur(60px)', transform: 'translate(-25%,-25%)' }} />

          {[{ e: '🎬', x: '7%', y: '22%' }, { e: '📺', x: '87%', y: '18%' }, { e: '🎥', x: '80%', y: '72%' }, { e: '⭐', x: '10%', y: '70%' }].map((item, i) => (
            <motion.div key={i} className="absolute text-5xl opacity-20 pointer-events-none select-none"
              style={{ left: item.x, top: item.y }}
              animate={{ y: [0, -20, 0], rotate: [0, 12, -12, 0] }}
              transition={{ duration: 4 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}>
              {item.e}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center">
              <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }} className="text-8xl mb-5">🎥</motion.div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-5 leading-tight" style={{ color: '#1A1F2E' }}>
                Video{' '}
                <span style={{ background: 'linear-gradient(135deg, #D32F2F, #F57C00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Gallery
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark leading-relaxed max-w-xl mx-auto">
                Watch fun videos, tutorials, and behind-the-scenes content from The Tiny Times! 📺
              </p>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
              {videoStats.map((stat, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="text-center rounded-2xl p-5"
                  style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}18` }}>
                  <div className="text-4xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-display font-bold mb-1" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="text-xs text-neutral-gray-dark font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Video */}
        {featuredVideo && (
          <section className="py-14 md:py-20" style={{ background: '#F5F7FA' }}>
            <div className="container mx-auto px-4">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-5xl mx-auto">
                <div className="text-center mb-8">
                  <span className="inline-block px-4 py-2 rounded-full font-display font-bold text-sm text-white mb-4"
                    style={{ background: '#D32F2F' }}>⭐ Featured Video</span>
                  <h2 className="text-3xl md:text-4xl font-display font-bold" style={{ color: '#1A1F2E' }}>{featuredVideo.title}</h2>
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <VideoEmbed videoId={featuredVideo.youtubeId} title={featuredVideo.title} />
                </div>
                <div className="mt-5 text-center">
                  <p className="text-lg text-neutral-gray-dark max-w-2xl mx-auto">{featuredVideo.description}</p>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Category Filter */}
        <section className="py-6 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => {
                const info = categoryInfo[category] || { icon: '🎥', label: category };
                const isActive = activeCategory === category;
                return (
                  <motion.button key={category} onClick={() => setActiveCategory(category)}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-button font-display font-semibold text-sm transition-all"
                    style={isActive
                      ? { background: '#D32F2F', color: 'white', boxShadow: '0 4px 14px rgba(211,47,47,0.3)' }
                      : { background: 'white', color: '#D32F2F', border: '1.5px solid rgba(211,47,47,0.25)' }
                    }>
                    <span className="text-lg">{info.icon}</span>
                    <span className="capitalize">{info.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="py-14 md:py-24" style={{ background: '#FAFAFA' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>All Videos 🎬</h2>
              <p className="text-lg text-neutral-gray-dark max-w-2xl mx-auto">Click on any video to watch it in full!</p>
            </motion.div>

            <VideoGrid videos={filteredVideos} onVideoSelect={setSelectedVideo} />

            {filteredVideos.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                <div className="text-8xl mb-4">📭</div>
                <h3 className="text-2xl font-display font-bold mb-2" style={{ color: '#1A1F2E' }}>No videos in this category</h3>
                <p className="text-neutral-gray-dark">Check back soon or try a different category!</p>
              </motion.div>
            )}
          </div>
        </section>

        {/* What You'll Find */}
        <section className="py-14 md:py-24" style={{ background: 'linear-gradient(160deg, #E8F5E9, #FFF9E6)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>What You'll Find 🎯</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Our videos cover a variety of fun and educational topics!</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {videoTypes.map((type, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: index * 0.08 }} whileHover={{ y: -8 }}
                  className="rounded-2xl p-6 text-center bg-white"
                  style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: `1px solid ${type.color}18` }}>
                  <div className="text-6xl mb-4">{type.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-3" style={{ color: type.color }}>{type.title}</h3>
                  <p className="text-sm text-neutral-gray-dark leading-relaxed">{type.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* YouTube CTA */}
        <section className="py-16" style={{ background: '#CC0000' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center text-white">
              <div className="text-7xl mb-5">📺</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">Subscribe to Our Channel!</h2>
              <p className="text-xl text-white/90 mb-8">Never miss a new video! Subscribe to The Tiny Times on YouTube.</p>
              <motion.a href="https://youtube.com/@tinytimes" target="_blank" rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 bg-white text-[#CC0000] px-10 py-4 rounded-button font-display font-bold text-xl shadow-lg hover:shadow-xl transition-all">
                <FaYoutube className="text-2xl" />
                Subscribe on YouTube
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Have an Idea */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">💡</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Have a Video Idea?</h2>
              <p className="text-xl text-neutral-gray-dark mb-8 max-w-2xl mx-auto">
                We love hearing from our viewers! Tell us what kind of videos you'd like to see next.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a href="https://wa.me/919618377750" target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.3)' }}>
                  <FaWhatsapp className="text-2xl" /> Share Your Idea 📝
                </motion.a>
                <motion.a href="/subscribe" whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{ background: 'white', color: '#D32F2F', border: '2px solid rgba(211,47,47,0.25)' }}>
                  Subscribe Now 🚀
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">🎉</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Ready for More Fun?</h2>
              <p className="text-xl text-white/85 mb-8">Subscribe to The Tiny Times and get weekly stories, puzzles, and activities!</p>
              <motion.a href="https://wa.me/919618377750" target="_blank" rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 px-10 py-4 rounded-button font-display font-bold text-xl text-white"
                style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}>
                <FaWhatsapp className="text-2xl" /> Subscribe Today 📬
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Video Modal */}
        {selectedVideo && (
          <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
        )}
      </div>
    </PageTransition>
  );
};

const VideoModal = ({ video, onClose }) => {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => { document.body.style.overflow = 'unset'; window.removeEventListener('keydown', handleEscape); };
  }, [onClose]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90" onClick={onClose}>
      <button onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl">
        <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
          <VideoEmbed videoId={video.youtubeId} title={video.title} autoplay={true} />
        </div>
        <div className="mt-4 text-white text-center">
          <h3 className="text-2xl font-display font-bold mb-1">{video.title}</h3>
          <p className="text-white/80 text-sm">{video.description}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Video;